import { dueStatus, type DueStatus } from '@/lib/dueStatus';
import type { BadgeTone } from '@/components';

/**
 * Status predicates and end-date math for equipment requests.
 *
 * Extracted from EquipmentPage so it can be tested: `apps/web` runs vitest in a
 * node environment with no jsdom, so component behaviour is untestable and pure
 * logic is pulled into a module like this instead.
 *
 * ⚠️ BRANCH ON `code` / `phase`, NEVER ON `label`. Labels are editable data —
 * the 2026-08-06 status overhaul renamed several, and label-gated behaviour
 * would have silently deleted the Call Off and Extend buttons (#306).
 */

export type StatusLike = { code?: string; label?: string; phase?: string } | null | undefined;

/** A request is finished when its status sits in the Closed phase. Phase comes
 *  from the database, so retiring or adding a terminal status needs no code
 *  change — this used to be a literal list of two labels. */
export const isCompleted = (status: StatusLike) => status?.phase === 'Closed';

/** Which status offers Call Off / Extend. Keyed on code, so rewording a label
 *  can never remove a button (#306). */
export const isOnSite    = (status: StatusLike) => status?.code === 'on_site';
export const isCalledOff = (status: StatusLike) => status?.code === 'called_off';

/**
 * Is this rental at or past its end date, and still actually out? (#319)
 *
 * The equipment manager asked for "a notification when a request has reached its end date —
 * an email or some visual that shows on the equipment page". This is the visual
 * half. The email is separate work with its own decisions: who receives it, how
 * often, and how it avoids re-alerting every day forever.
 *
 * Returns null when there is nothing to flag, so callers can fall straight
 * through to their normal styling.
 *
 * ⚠️ CALLED OFF COUNTS AS DONE HERE, even though its phase is Active. Once the
 * call-off is in, the pickup runs on somebody else's clock and the end date has
 * stopped meaning anything. Colouring those red would leave a permanent wall of
 * red on precisely the rows already dealt with, which is how a warning turns
 * into wallpaper.
 *
 * Thresholds come from lib/dueStatus rather than being re-derived, so equipment
 * agrees with RFIs and submittals about what "overdue" and "due soon" mean.
 */
export function endDateStatus(
  status: StatusLike,
  endDate: string | null | undefined,
): DueStatus | null {
  if (isCompleted(status) || isCalledOff(status)) return null;
  if (!endDate) return null;
  const s = dueStatus(endDate);
  return s.tone === 'danger' || s.tone === 'warning' ? s : null;
}

/** Tone → text colour, matching the RFI and submittal due-date cells. */
export const DUE_COLOR: Record<BadgeTone, string | undefined> = {
  danger: 'var(--ew-danger)',
  warning: '#92400E',
  neutral: undefined,
  success: undefined,
  info: undefined,
};
